import { useEffect, useState } from "react";
import { api, fmtAUD } from "@/lib/api";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { FadeUp } from "@/components/Reveal";
import PreviewBanner from "./PreviewBanner";
import { ShieldCheck } from "lucide-react";

// Long-scroll storytelling product page
export default function ProductStory() {
  const [p, setP] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 240]);

  useEffect(() => {
    api.get("/products/pixel-9-pro-xl").then((r) => setP(r.data));
  }, []);
  if (!p) return <div className="pt-40 px-6">Loading…</div>;

  return (
    <div className="pt-24" data-testid="preview-product-story">
      <PreviewBanner title="Long-Scroll Story" kind="Product page" />

      {/* Full-bleed hero */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end px-6 md:px-10 pb-20 overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={p.image_url} alt="" className="absolute inset-0 w-full h-full object-cover img-clip phantom" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-[#030303]/50" />
        </motion.div>

        <div className="relative z-10 max-w-3xl">
          <div className="overline">Chapter · {p.tier.toUpperCase()}</div>
          <h1 className="font-display uppercase text-6xl md:text-9xl tracking-tight leading-[0.85] mt-6">
            {p.name.split("//")[0]}
          </h1>
          <div className="mt-6 text-[#EAEAEA] text-lg md:text-2xl max-w-2xl">{p.tagline}</div>
          <div className="mt-10 flex items-center gap-6">
            <div>
              <div className="overline">Price</div>
              <div className="font-display text-4xl">{fmtAUD(p.price_aud)}</div>
            </div>
            <button className="btn-vault danger">Buy now</button>
            <button className="btn-vault">+ Cart</button>
          </div>
        </div>
      </section>

      {/* Chapter 01 — What it is */}
      <section className="px-6 md:px-10 py-32 md:py-48 border-t border-[#1A1A1A]">
        <FadeUp>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="col-span-12 md:col-span-3">
              <div className="font-display text-[9rem] leading-none text-[#222]">01</div>
              <div className="overline mt-4">What it is</div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95]">
                A Google Pixel with every Google touch-point severed, muted, or removed.
              </h2>
              <p className="mt-10 max-w-2xl text-[#888] text-lg leading-relaxed">{p.long_description}</p>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Chapter 02 — Specs as prose */}
      <section className="px-6 md:px-10 py-32 md:py-48 border-t border-[#1A1A1A] bg-[#050505]">
        <FadeUp>
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-3">
              <div className="font-display text-[9rem] leading-none text-[#222]">02</div>
              <div className="overline mt-4">Under the hood</div>
            </div>
            <div className="col-span-12 md:col-span-9 space-y-6 text-[#EAEAEA] text-xl md:text-2xl leading-relaxed max-w-3xl">
              {Object.entries(p.specs).map(([k, v]) => (
                <div key={k} className="border-b border-[#1A1A1A] pb-6">
                  <span className="overline text-[#666]">{k}</span>
                  <div className="mt-2 font-mono text-2xl md:text-3xl">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Chapter 03 — What comes with it */}
      <section className="px-6 md:px-10 py-32 md:py-48 border-t border-[#1A1A1A]">
        <FadeUp>
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-3">
              <div className="font-display text-[9rem] leading-none text-[#222]">03</div>
              <div className="overline mt-4">Included</div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <ol className="space-y-8 counter-reset-[phantom]" data-testid="story-features">
                {p.features.map((f, i) => (
                  <li key={f} className="flex items-baseline gap-6 border-b border-[#1A1A1A] pb-6">
                    <span className="font-mono text-[#444] text-sm">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[#EAEAEA] text-xl md:text-2xl font-display uppercase tracking-tight">{f}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Closing CTA */}
      <section className="px-6 md:px-10 py-32 md:py-48 border-t border-[#1A1A1A] bg-[#050505]">
        <FadeUp>
          <div className="text-center max-w-3xl mx-auto">
            <div className="overline">— Ξ 04 —</div>
            <h2 className="font-display uppercase text-5xl md:text-8xl tracking-tight leading-[0.9] mt-8">
              Ready to<br />disappear?
            </h2>
            <div className="mt-12 flex items-center justify-center gap-4">
              <button className="btn-vault danger">Buy · {fmtAUD(p.price_aud)}</button>
              <button className="btn-vault">Add to cart</button>
            </div>
            <div className="mt-8 flex items-center justify-center gap-2 text-[#444] text-xs overline">
              <ShieldCheck size={14} /> Hand hardened in Sydney · Ships 5–7 business days
            </div>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
